//Pure Functions:
import { CometChat } from "@cometchat-pro/chat";
import { COMETCHAT_CONSTANTS } from "../configs/keys"
import axios from "axios";
import isEqual from "lodash/isEqual";

const updateContactList = (user, receiverId) => {
    const contactList = user?.metadata?.contactList || [];

    const isSameContactListDataPresent = contactList.length > 0 && contactList.some((item) => {
        const { contactedFrom, contactedTo } = item;
        return (
            (contactedFrom === user.uid && contactedTo === receiverId) ||
            (contactedFrom === receiverId && contactedTo === user.uid)
        );
    });

    let updateMetaData = {
        name: user.name,
        uid: user.uid,
        metadata: user.metadata
    };

    if (!isSameContactListDataPresent) {
        updateMetaData = {
            ...updateMetaData,
            metadata: {
                ...user.metadata,
                contactList: [
                    ...user.metadata.contactList,
                    { contactedFrom: user.uid, contactedTo: receiverId }
                ]
            }
        };
        const isContactListUpdated = { status: true, type: 'newUserUid' };
        return { isContactListUpdated, updateMetaData }
    } else {
        const isContactListUpdated = { status: false, type: 'none' };
        return { isContactListUpdated, updateMetaData }
    }

}

const isMetaDataUpdatable = (user, receiverId, orderContextData, updateOrderMetaObject) => {
    const allOrderAccepted = orderContextData &&
        orderContextData.referenceUid === receiverId &&
        user.metadata.allOrderAccepted ? user.metadata.allOrderAccepted : [];

    const isSameOrderDataPresent = allOrderAccepted.length > 0 && allOrderAccepted.filter((item) => {
        const { to_uid, from_uid, referenceOrderId } = item;

        const checkToUser = to_uid === user.uid || to_uid === receiverId
        const checkFromUser = from_uid === user.uid || from_uid === receiverId
        const finalUserCheck = checkToUser || checkFromUser
        const checkSameOrder = referenceOrderId === orderContextData.referenceOrder
        const isOrderIdEmpty = orderContextData.referenceOrder === '' ||
            orderContextData.referenceOrder?.substring(0, 3)?.toUpperCase() !== 'ORD'
        const userCheckWhenNoOrderId = isOrderIdEmpty ? finalUserCheck : finalUserCheck && checkSameOrder

        return userCheckWhenNoOrderId;
    });

    let updateMetaData = {
        name: user.name,
        uid: user.uid,
        metadata: user.metadata
    };

    let isMetaDataChanged = { status: false, type: 'none' };
    let isContactListUpdated = { status: false, type: 'none' };

    const updateContactRes = updateContactList(user, receiverId)
    if (updateContactRes.status) {
        isMetaDataChanged = updateContactRes.isContactListUpdated
        updateMetaData = updateContactRes.updateMetaData
    }

    if (!isSameOrderDataPresent.length) {
        //no similar metaData between contacted users and with orderId -> creating it
        updateMetaData = {
            ...updateMetaData,
            metadata: {
                ...user.metadata,
                allOrderAccepted: [
                    ...user.metadata.allOrderAccepted,
                    ...[updateOrderMetaObject],

                ]
            }
        };
        isMetaDataChanged = { status: true, type: 'newOrderId' };
    } else {
        //test any key has changed
        //objects in js equate bases of reference img not deep contents, use lodash _.isEqual method
        const areMetaDataSame = isEqual(updateOrderMetaObject, isSameOrderDataPresent[isSameOrderDataPresent.length - 1])
        const allMetaNeededCheck = Object.keys(updateOrderMetaObject).length > 0 &&
            isSameOrderDataPresent.length > 0 && user?.metadata?.allOrderAccepted?.length > 0

        if (!areMetaDataSame && allMetaNeededCheck) {
            let existingOrderRef = [];
            const otherArrElements = user.metadata.allOrderAccepted.filter((item) => {
                const isReferringToSameObj = isSameOrderDataPresent.length > 0 && item === isSameOrderDataPresent[isSameOrderDataPresent.length - 1]
                return !isReferringToSameObj
            })

            if (otherArrElements.length) {
                existingOrderRef = [otherArrElements[otherArrElements.length - 1]]
            }

            const isNewExistingArrSame = isEqual(existingOrderRef, [updateOrderMetaObject])
            if (!isNewExistingArrSame) {
                if (existingOrderRef.length) {
                    updateMetaData = {
                        ...updateMetaData,
                        metadata: {
                            ...user.metadata,
                            allOrderAccepted: [
                                ...existingOrderRef,
                                ...[updateOrderMetaObject],
                            ]
                        }
                    };
                    isMetaDataChanged = { status: true, type: 'updateMetaData' };
                } else {
                    updateMetaData = {
                        ...updateMetaData,
                        metadata: {
                            ...user.metadata,
                            allOrderAccepted: [
                                ...[updateOrderMetaObject],
                            ]
                        }
                    }
                    isMetaDataChanged = { status: true, type: 'newMetaData' };
                }

            } else {
                isMetaDataChanged = { status: false, type: 'existingMeta' }
            }
        } else {
            isMetaDataChanged = { status: false, type: 'existingUserData' }
        }
    }

    const newUpdatedMetaData = isMetaDataChanged.status === true || isContactListUpdated.status === true ? updateMetaData : {}
    return { isMetaDataChanged, isContactListUpdated, newUpdatedMetaData }
}

const metaDataFlow = (updatedMetaData, userContext, orderContext) => {
    /* allOrderAccepted propTypes :
        {
            to_uid: receiverId,
            from_uid: my_uid,
            referenceOrderId: ORDER_ID,
            orderMarkedTo: 'none', //none/ 'OrderId'/ status
            status: 'ongoing',
            flush: false, 
            orderMarkedTimeline: '',
            additionalData: []
        };
    
        newMetaData for allOrderAccepted related data flow : 
            orderMarkedTo flow:
                Default: none
                Can be updated to :
                none -> OrderId -> status Key
                orderId -> status key
                none -> status key
    
            status key flow:
                Default : 'unassigned'
                unassigned -> 'ongoing'
                unassigned -> 'rejected'
                ongoing -> completed
                ongoing -> rejected
    
            flush key flow:
                Default : true
                status key 'ongoing'/'completed' -> false
                status key else -> true
                whenever true after 10 days from orderMarkedTimeline -> flush data
                	
    */

    const originalUserMeta = userContext?.metadata?.allOrderAccepted?.length ? userContext.metadata.allOrderAccepted : []
    const updatedUserMeta = updatedMetaData?.metadata?.allOrderAccepted?.length ? updatedMetaData.metadata.allOrderAccepted : []
    const areMetaEqual = isEqual(originalUserMeta, updatedUserMeta)

    if (areMetaEqual) {
        //Don't wanna update if there's no change
        return false;
    } else if (updatedUserMeta?.length && Object.keys(orderContext).length > 0) {
        const { referenceUid, referenceOrder } = orderContext
        const filteredMetaData = (arr, refUid, testOnOrder) => {
            return arr.filter((item) => {
                const { to_uid, from_uid, referenceOrderId } = item;
                const similarRefUidExists = to_uid === refUid || from_uid === refUid
                const similarRefOrderExists = testOnOrder ? referenceOrderId === referenceOrder : true
                return similarRefUidExists && similarRefOrderExists
            })
        }

        const similarOrderMatches = referenceUid ? filteredMetaData(updatedUserMeta, referenceUid, true) : []
        const oriOrderMeta = userContext?.uid ? filteredMetaData(originalUserMeta, userContext.uid, false) : []

        //always take the latest obj reference
        const freshlyAddedMatch = similarOrderMatches?.length ? similarOrderMatches[similarOrderMatches.length - 1] : {}
        const oriMetaMatch = oriOrderMeta?.length ? oriOrderMeta[oriOrderMeta.length - 1] : {}

        if (Object.keys(freshlyAddedMatch).length) {
            if (!Object.keys(oriMetaMatch).length) {
                // if no data is there always improvise
                return true;
            } else {
                //Mappings shall be always valid to itself
                const validOrderMarkedToTransitions = {
                    'none': ['client_approach', 'uid', 'rejected'],
                    'client_approach': ['uid', 'cancelled'],
                    'uid': ['cancelled'],
                    'rejected': [],
                    'cancelled': ['uid']
                };

                const validStatusTransitions = {
                    'unassigned': ['ongoing', 'rejected'],
                    'ongoing': ['completed', 'cancelled'],
                    'completed': ['ongoing'],
                    'rejected': [] //no other transition
                };

                //test
                const validOrderIdTransitions = {
                    '': ['orderId'],
                    'orderId': [], //no other transition
                };

                const checkValidTransition = (currentType, newType, validationType) => {
                    if (validationType[currentType] && validationType[currentType].includes(newType)) {
                        return true;
                    } else {
                        return false;
                    }
                }

                const getTestOrderTo = (string) => {
                    const stringType = string?.substring(0, 2)?.toUpperCase() === 'ex' ? 'uid' : string
                    return stringType
                }

                const getTestOrderId = (string) => {
                    const stringType = string?.substring(0, 3)?.toUpperCase() === 'ORD' ? 'orderId' : string
                    return stringType
                }

                const oriOrderToRef = getTestOrderTo(oriMetaMatch.orderMarkedTo);
                const newOrderToRef = getTestOrderTo(freshlyAddedMatch.orderMarkedTo);

                const orderMarkedToValid = checkValidTransition(oriOrderToRef, newOrderToRef, validOrderMarkedToTransitions)
                const orderStatusValid = checkValidTransition(oriMetaMatch.status, freshlyAddedMatch.status, validStatusTransitions)
                const orderIdValid = checkValidTransition(
                    getTestOrderId(oriMetaMatch.referenceOrderId),
                    getTestOrderId(freshlyAddedMatch.referenceOrderId),
                    validOrderIdTransitions
                )

                return orderIdValid && (orderMarkedToValid && orderStatusValid) //test for && or ||
            }
        } else {
            //never update to empty values
            return false;
        }
    } else {
        //Some other metadata updated
        return true;
    }

}

export const cometUserUpdate = (newMetaObjectContext, userContext, receiverId, orderContext) => {
    let updatedMetaData = {
        isMetaDataChanged: { status: false, type: 'none' },
        newUpdatedMetaData: {},
        updateWithOutCheck: false,
    }

    if (userContext !== undefined) {
        if (orderContext === 'updateContact') {
            const updateContactRes = updateContactList(userContext, receiverId)
            if (updateContactRes?.isContactListUpdated?.status && updateContactRes?.isContactListUpdated?.type === 'newUserUid') {
                updatedMetaData = {
                    isMetaDataChanged: updateContactRes.isContactListUpdated,
                    newUpdatedMetaData: updateContactRes.updateMetaData,
                    updateWithOutCheck: true
                }
            }
        } else {
            const updatableMetaRes = isMetaDataUpdatable(userContext, receiverId, orderContext, newMetaObjectContext)
            const updateDataWithoutCheck = (updatableMetaRes?.isMetaDataChanged?.status &&
                updatableMetaRes?.isMetaDataChanged?.type === 'newOrderId') ||
                (updatableMetaRes?.isContactListUpdated?.status &&
                    updatableMetaRes?.isContactListUpdated?.type === 'newUserUid')
            updatedMetaData = {
                ...updatableMetaRes,
                updateWithOutCheck: updateDataWithoutCheck
            }
        }
    } else if (newMetaObjectContext && Object.keys(newMetaObjectContext).length) {
        updatedMetaData = {
            ...updatedMetaData,
            newUpdatedMetaData: newMetaObjectContext,
            updateWithOutCheck: true
        }
    }

    const isUpdatableDataPresent = updatedMetaData?.newUpdatedMetaData && Object.keys(updatedMetaData?.newUpdatedMetaData).length > 0
    const orderMetaFollowsDataFlow = updatedMetaData.updateWithOutCheck ?
        isUpdatableDataPresent :
        isUpdatableDataPresent && metaDataFlow(updatedMetaData, userContext, orderContext)

    if (orderMetaFollowsDataFlow) {
        return new Promise((resolve, reject) => {
            console.log("~~~users fetched: cometUserUpdate calling CometChat.updateUser", {
                updatedMetaData: updatedMetaData?.newUpdatedMetaData,
                userContext,
                orderContext,
            });
            //cometUserData needs name and uid to update user
            CometChat.updateUser(updatedMetaData?.newUpdatedMetaData, COMETCHAT_CONSTANTS.AUTH_KEY).then(
                user => {
                    //User updated successfully
                    resolve({ status: true, data: user })
                },
                error => {
                    resolve({ status: false, data: error })
                }
            );
        })
    } else return new Promise((resolve, reject) => resolve({ status: false, data: 'meta data already exists' }))
}

export const cometMessageListener = (uid) => {
    return new Promise((resolve, reject) => {
        CometChat.addMessageListener(uid, new CometChat.MessageListener({
            onTextMessageReceived: message => {
                // Handle incoming messages
                resolve({ status: true, type: 'onTextMessageReceived', data: message })
            },
            onUserOnline: onlineUser => {
                // Handle user online status
                resolve({ status: true, type: 'onUserOnline', data: onlineUser })
            },
            onUserOffline: offlineUser => {
                // Handle user offline status
                resolve({ status: true, type: 'onUserOffline', data: offlineUser })
            },
        }));
    })
}

export const cometLoggedInUser = (uid) => {
    return new Promise((resolve, reject) => {
        CometChat.getLoggedinUser().then(
            loggedUser => {
                if (!loggedUser) {
                    resolve({ status: false, type: 'noUser', data: uid })
                } else {
                    if (loggedUser?.uid && loggedUser.uid !== uid) {
                        resolve({ status: false, data: uid })
                    } else {
                        resolve({ status: true, data: loggedUser })
                    }
                }
            })
    })
}

export const cometAuthToken = (uid, authToken) => {
    return new Promise((resolve, reject) => {
        let config = {
            method: 'get',
            maxBodyLength: Infinity,
            url: `https://${COMETCHAT_CONSTANTS.APP_ID}.api-${COMETCHAT_CONSTANTS.REGION}.cometchat.io/v3/users/${uid}/auth_tokens/${authToken}`,
            headers: {
                'accept': 'application/json',
                'apikey': COMETCHAT_CONSTANTS.AUTH_KEY
            }
        };

        axios.request(config)
            .then(async (response) => { resolve({ status: true, data: response }) })
            .catch((error) => { resolve({ status: false, data: error }) })
    })
}

export const cometLoginUser = (uid) => {
    return new Promise((resolve, reject) => {
        CometChat.login(uid, COMETCHAT_CONSTANTS.AUTH_KEY).then(
            (user) => {
                // Perform actions after successful login, if needed
                if (user.uid !== uid) {
                    window.location.reload()
                } else {
                    resolve({ status: true, data: user })
                }
            },
            (error) => {
                // Handle login failure appropriately
                if (error?.name === 'COMETCHAT_INITIALIZATION_NOT_DONE') {
                    resolve({ status: false, type: 'notInitialized', data: error.name })
                } else {
                    resolve({ status: false, data: error })
                }
            }
        )
    })
}

export const cometGetUser = (uid) => {
    console.log("~~~users fetched: cometGetUser called", { uid });
    return new Promise((resolve, reject) => {
        CometChat.getUser(uid).then(
            (user) => {
                // Successfully retrieved user information
                resolve({ status: true, data: user })
            },
            (error) => {
                // Handle errors
                resolve({ status: false, data: error })
            }
        ).catch((err) => {
            resolve({ status: false, data: err })
        })
    })
}

export const cometUserExists = (uid) => {
    console.log("~~~users fetched: cometUserExists called", { uid });
    return new Promise((resolve, reject) => {
        CometChat.getUser(uid).then(
            user => {
                // User already exists!
                resolve({ status: true, data: user })
            },
            error => {
                // Create the user.
                resolve({ status: false, type: 'createNewUser', data: error })
            }
        ).catch((err) => {
            resolve({ status: false, data: err })
        })
    })
}

export const cometCreateUser = (user, uid) => {
    console.log("~~~users fetched: cometCreateUser called", { uid, avatar: user.avatar, name: user.name });
    return new Promise((resolve, reject) => {
        CometChat.createUser(user, COMETCHAT_CONSTANTS.AUTH_KEY).then(
            (createdUser) => {
                // User created successfully
                resolve({ status: true, data: createdUser })
            },
            (error) => {
                // Error creating user
                if (error && error.code === 'ERR_UID_ALREADY_EXISTS') {
                    resolve({ status: false, type: 'userAlreadyExists', data: error.code })
                } else {
                    resolve({ status: false, data: error })
                }
            }
        );
    })
}

export const cometLogoutUser = () => {
    return new Promise((resolve, reject) => {
        try {
            CometChat.logout().then(() => {
                // Additional logic, such as redirection or state updates, can go here
                resolve({ status: true, data: 'success' })
            }).catch((error) => {
                // console.error('CometChat logout error:', error);
                resolve({ status: false, data: 'failure' })
            });
            resolve({ status: false, data: 'failure' })
        } catch (err) {
            //cometChat err
        }

        setTimeout(() => {
            //if there is any uncertain result after this interval of time, resolve
            resolve({ status: false, data: 'failure' })
        }, 3500)
    })
}

export const cometChatInitialized = () => {
    return new Promise((resolve, reject) => {
        CometChat.isInitialized().then(() => {
            resolve({ status: true, data: 'success' })
        }).catch((err) => {
            resolve({ status: false, data: err })
        })
    })
}